import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Components/Header';
import { Container, Row, Col, Card, CardText, CardBody, CardHeader, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { Spinner } from 'react-bootstrap';
import { BASE_URL } from '../config';

export default function DoctorProfileEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState({});
  const [originalDoctor, setOriginalDoctor] = useState({});
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch doctor profile data
  useEffect(() => {
    const fetchDoctorProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}DoctorProfile?id=${id}`, {
          method: 'POST',
          headers: { 'Accept': 'text/plain' },
        });

        if (!response.ok) throw new Error('Failed to fetch doctor profile.');

        const data = await response.json();
        if (data.doctorList) {
          const doctorData = data.doctorList[0];
          setLoading(false);
          setDoctor(doctorData);
          setOriginalDoctor(doctorData); // Keep original state for resetting

          // Split doctorDutyTime into startTime and endTime
          if (doctorData.doctorDutyTime) {
            const [start, end] = doctorData.doctorDutyTime.split(' - ');
            setDoctor((prevDoctor) => ({
              ...prevDoctor,
              startTime: start,
              endTime: end,
            }));
          }

          // Convert DateOfBirth to the correct format (YYYY-MM-DD)
          if (doctorData.dateOfBirth) {
            const date = new Date(doctorData.dateOfBirth);  // Create a Date object
            const formattedDate = date.toISOString().split('T')[0];  // Convert to YYYY-MM-DD format
            setDoctor((prevDoctor) => ({
              ...prevDoctor,
              dateOfBirth: formattedDate,
            }));
          }
        }
      } catch (error) {
        setLoading(false);
        console.error('Error fetching doctor profile:', error);
        alert('Failed to fetch doctor profile.');
      }
    };

    fetchDoctorProfile();
  }, [id]);


  // Fetch specialists
  useEffect(() => {
    const fetchSpecialists = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}Specialist_List`, {
          headers: { accept: 'application/json' },
        });
        const data = await response.json();
        setSpecialists(data.getSpecialistDetail || []); // Update the specialists state
      } catch (error) {
        setLoading(false);
        console.error('Error fetching specialists:', error);
      }
    };

    fetchSpecialists();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setDoctor((prevDoctor) => ({
      ...prevDoctor,
      [name]: value,
    }));
  };


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      setDoctor((prevDoctor) => ({
        ...prevDoctor,
        image: file,              // ✅ Store actual File
        imagePreview: previewUrl,
      }));
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    const {
      name,
      allqualifications,
      network,
      contactNumber,
      city,
      specialization,
      gender,
      address,
      cnic,
      dateOfBirth,
      doctorDutyTime,
      image,
      startTime,
      endTime
    } = doctor;

    if (!name || !allqualifications || !contactNumber || !city || !specialization || !gender || !address || !cnic || !dateOfBirth || !image || !startTime || !endTime || !network) {
      alert('Please fill in all required fields.');
      return;
    }

    const combinedDutyTime = `${startTime} - ${endTime}`;

    const specializationId = specialists.find(s => s.specialization_Name === specialization)?.id;
    if (!specializationId) {
      alert('Invalid specialization selected.');
      return;
    }

    const formData = new FormData();
    formData.append('Gender', gender);
    formData.append('DoctorDutyTime', combinedDutyTime);
    formData.append('Allqualifications', allqualifications);
    formData.append('Cnic', cnic);
    formData.append('SpecializationId', specializationId.toString());
    formData.append('City', city);
    formData.append('MobileNumber', contactNumber);
    formData.append('Network', network);
    formData.append('Docid', id);
    formData.append('Address', address);
    formData.append('DateOfBirth', dateOfBirth);
    formData.append('FirstName', name);
    formData.append('ImgLink', image); // Directly pass the File object

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}DoctorProfileEdit`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain', // Do NOT set 'Content-Type' here; fetch will set it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to update doctor profile.');

      const data = await response.json();
      if (data.responseCode === '0000') {
        alert('Profile updated successfully!');
        navigate(`/DoctorProfile/${id}`);
      } else {
        alert('Failed to update profile.');
      }
    } catch (error) {
      console.error('Error updating doctor profile:', error);
      alert('An error occurred while updating the profile.');
    } finally {
      setLoading(false);
    }
  };



  const handleCancel = () => {
    setDoctor(originalDoctor); // Reset to original state
    navigate(`/DoctorProfile/${id}`);
  };

  return (
    <Container fluid className="h-custom py-1">
      <Header />
      {
        loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner size="lg" />
          </div>
        ) : (
          <Row className="justify-content-center align-items-center mt-3">
            <Col md="12">
              <Card style={{ borderRadius: '12px' }}>
                <CardHeader className="text-center bg_primary text-white">
                  <h5 className="fw-bold m-1">Edit Doctor Profile</h5>
                </CardHeader>

                <CardBody className="p-0">
                  <Row className="mt-4">
                    <Col lg="4" className="text-center">
                      <Card className="mb-4 shadow-none">
                        <CardBody>
                          {/* <img
                            src={doctor.image || '/default-avatar.png'}
                            alt="avatar"
                            className="rounded-circle img-thumbnail"
                            style={{ width: '200px', height: '200px', borderColor: "#50c4cc" }}
                          /> */}
                          <img
                            src={doctor.imagePreview || (typeof doctor.image === 'string' ? doctor.image : '/default-avatar.png')}
                            alt="avatar"
                            className="rounded-circle img-thumbnail"
                            style={{ width: '200px', height: '200px', borderColor: "#50c4cc" }}
                          />

                          <FormGroup className="mt-3">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col lg="8">
                      <Card className="m-3 shadow-none" style={{ borderRadius: '25px' }}>
                        <CardBody className='p-0'>
                          <Form onSubmit={handleSubmit}>
                            <FormGroup row>
                              <Label sm="3">Full Name <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="text"
                                  name="name"
                                  value={doctor.name || ''}
                                  onChange={handleInputChange}
                                  placeholder="Full Name"
                                />
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Label sm="3">Qualifications <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="text"
                                  name="allqualifications"
                                  value={doctor.allqualifications || ''}
                                  onChange={handleInputChange}
                                  placeholder="allqualifications"
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">CNIC <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="tel"
                                  name="cnic"
                                  value={doctor.cnic || ''}
                                  onChange={handleInputChange}
                                  placeholder="CNIC"
                                  maxLength="13"
                                  pattern="\d{13}"
                                  required
                                  inputMode="numeric"
                                  onInput={(e) => {
                                    // Allow only numbers by removing any non-numeric characters
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                  }}
                                />
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Label sm="3">Gender <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="select"
                                  name="gender"
                                  value={doctor.gender || ''}
                                  onChange={handleInputChange}
                                >
                                  <option value="" disabled>Select Gender</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </Input>
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Label sm="3">Network <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="select"
                                  name="network"
                                  value={doctor.network || ''}
                                  onChange={handleInputChange}
                                >
                                  <option value="" hidden disabled>Select Network</option>
                                  <option value="zong">Zong</option>
                                  <option value="jazz">Jazz</option>
                                  <option value="ufone">Ufone</option>
                                  <option value="telenor">Telenor</option>
                                </Input>
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Label sm="3">Phone <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="tel"
                                  name="contactNumber"
                                  value={doctor.contactNumber || ''}
                                  onChange={handleInputChange}
                                  placeholder="Phone"
                                  maxLength="12"
                                  minLength="11" // Ensures at least 11 characters
                                  pattern="\d{11,12}" // Only allows 12 or 12 digits.
                                  inputMode="numeric"
                                  onInput={(e) => {
                                    // Allow only numbers by removing any non-numeric characters
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                  }}
                                  required
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">Specialization <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="select"
                                  name="specialization"
                                  value={doctor.specialization || ''}
                                  onChange={handleInputChange}
                                >
                                  <option value="">Select Specialization</option>
                                  {specialists.length > 0 ? (
                                    specialists.map((specialist) => (
                                      <option key={specialist.id} value={specialist.specialization_Name}>
                                        {specialist.specialization_Name}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="">No specializations available</option>
                                  )}
                                </Input>
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">Address <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="text"
                                  name="address"
                                  value={doctor.address || ''}
                                  onChange={handleInputChange}
                                  placeholder="Address"
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">City <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="text"
                                  name="city"
                                  value={doctor.city || ''}
                                  onChange={handleInputChange}
                                  placeholder="City"
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">Date of Birth <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <Input
                                  type="date"
                                  name="dateOfBirth"
                                  value={doctor.dateOfBirth || ''}
                                  onChange={handleInputChange}
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm="3">Duty Time <span className='text-danger'>*</span></Label>
                              <Col sm="9">
                                <div className="d-flex">

                                  <Input
                                    type="time"
                                    name="startTime"
                                    value={doctor.startTime || ''}
                                    onChange={handleInputChange}
                                    placeholder="Start Time"
                                    required
                                  />
                                  <span className="mx-2">-</span>
                                  {/* End Time Input */}
                                  <Input
                                    type="time"
                                    name="endTime"
                                    value={doctor.endTime || ''}
                                    onChange={handleInputChange}
                                    placeholder="End Time"
                                    required
                                  />
                                </div>
                              </Col>
                            </FormGroup>

                            <div className="text-end">
                              <Button color="info" className='me-2 btn_primary' type="submit" disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                              </Button>
                              <Button color="danger" onClick={handleCancel}>
                                Cancel
                              </Button>
                            </div>
                          </Form>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )
      }

    </Container>
  );
}
